// AeroTableEditor — 1D / 2D / 3D coefficient table editor.
//
// UX contract (Module 2)
// ----------------------
// • Accepts either JSON-shaped `LookupTable` ({axes, values}) or a CSV
//   paste. CSV is the fast path — the first row is the axis header
//   (e.g. `alpha_deg, CL` for 1D, `alpha_deg, beta_deg, CL` for 2D in
//   long format, etc.). Numeric columns following the axes form the
//   values tensor.
// • The visual grid is read-only in this iteration — editing individual
//   cells lands in Module 3 follow-up. Uploads replace the whole table
//   atomically.
// • A small Plotly plot is lazily imported ONLY when a table is
//   present; it degrades to a numeric summary if plotly.js can't
//   load (keeps the RTL tests fast and offline-safe).
import { useEffect, useMemo, useRef, useState } from "react";
import { UploadCloud, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function parseCsv(text) {
    const lines = (text || "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length < 2) {
        throw new Error("CSV must have a header row + at least one data row.");
    }
    const header = lines[0].split(/[;,]/).map((s) => s.trim());
    const rows = lines.slice(1).map((l, i) => {
        const parts = l.split(/[;,]/).map((s) => s.trim());
        if (parts.length !== header.length) {
            throw new Error(`Row ${i + 1}: expected ${header.length} fields, got ${parts.length}.`);
        }
        return parts.map((v) => {
            const n = Number(v);
            if (Number.isNaN(n)) {
                throw new Error(`Row ${i + 1}: "${v}" is not a number.`);
            }
            return n;
        });
    });
    if (header.length < 2) {
        throw new Error("CSV must have at least one axis column + one value column.");
    }
    // Last column is the value; every preceding column is an axis.
    const axisCols = header.slice(0, -1);
    const valueCol = header[header.length - 1];

    // 1-D fast path: single axis.
    if (axisCols.length === 1) {
        return {
            axes: [{ name: axisCols[0], unit: "", values: rows.map((r) => r[0]) }],
            values: rows.map((r) => r[1]),
            unit: "",
            source: "user",
            confidence: "provisional",
            citation: `CSV upload (${valueCol})`,
        };
    }

    // N-D: rows encode every combination; assemble axes + nested-list tensor.
    const axesUnique = axisCols.map((name, colIdx) => {
        const vals = Array.from(new Set(rows.map((r) => r[colIdx]))).sort((a, b) => a - b);
        return { name, unit: "", values: vals };
    });
    // Build a nested-list tensor shaped like the product of axis sizes.
    const shape = axesUnique.map((a) => a.values.length);
    const totalCells = shape.reduce((x, y) => x * y, 1);
    if (rows.length !== totalCells) {
        throw new Error(
            `Need ${totalCells} rows (product of axis lengths) for a ${shape.length}-D table; got ${rows.length}.`,
        );
    }
    const index = axisCols.map((_, colIdx) => {
        const vals = axesUnique[colIdx].values;
        const map = new Map(vals.map((v, i) => [v, i]));
        return (cell) => map.get(cell);
    });
    const makeEmpty = (dims) =>
        dims.length === 0
            ? 0
            : Array.from({ length: dims[0] }, () => makeEmpty(dims.slice(1)));
    const tensor = makeEmpty(shape);
    for (const row of rows) {
        let cursor = tensor;
        for (let i = 0; i < axisCols.length - 1; i += 1) {
            cursor = cursor[index[i](row[i])];
        }
        cursor[index[axisCols.length - 1](row[axisCols.length - 1])] = row[axisCols.length];
    }
    return {
        axes: axesUnique,
        values: tensor,
        unit: "",
        source: "user",
        confidence: "provisional",
        citation: `CSV upload (${valueCol})`,
    };
}

// ---------------------------------------------------------------------
// Plot (lazy)
// ---------------------------------------------------------------------
function QuickPlot({ table }) {
    const ref = useRef(null);
    const [ready, setReady] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!table || !ref.current) return undefined;
        (async () => {
            try {
                // `plotly.js-dist-min` is the pre-bundled Plotly (~800 KB)
                // shipped by Plotly for exactly this use case; webpack 5
                // treats it as a single pre-parsed blob, avoiding the
                // Node-polyfill and OOM issues of the `plotly.js` src tree.
                const Plotly = (await import("plotly.js-dist-min")).default;
                if (cancelled || !ref.current) return;
                let data;
                const layout = {
                    margin: { l: 40, r: 10, t: 10, b: 40 },
                    paper_bgcolor: "rgba(0,0,0,0)",
                    plot_bgcolor: "rgba(0,0,0,0)",
                    font: { color: "#cbd2d9", size: 10 },
                    xaxis: { gridcolor: "#253244" },
                    yaxis: { gridcolor: "#253244" },
                };
                if (table.axes.length === 1) {
                    data = [{
                        x: table.axes[0].values,
                        y: table.values,
                        mode: "lines+markers",
                        line: { color: "#3da9fc" },
                        marker: { color: "#3da9fc" },
                        name: table.citation || "values",
                    }];
                    layout.xaxis.title = table.axes[0].name;
                    layout.yaxis.title = table.citation || "value";
                } else if (table.axes.length === 2) {
                    data = [{
                        z: table.values,
                        x: table.axes[0].values,
                        y: table.axes[1].values,
                        type: "heatmap",
                        colorscale: "Viridis",
                    }];
                    layout.xaxis.title = table.axes[0].name;
                    layout.yaxis.title = table.axes[1].name;
                } else {
                    // 3D — flatten the first axis slice for quick plot.
                    const slice0 = Array.isArray(table.values) ? table.values[0] : [];
                    data = [{
                        z: slice0,
                        x: table.axes[1].values,
                        y: table.axes[2].values,
                        type: "heatmap",
                        colorscale: "Viridis",
                    }];
                    layout.xaxis.title = `${table.axes[1].name} (slice: ${table.axes[0].name}=${table.axes[0].values[0]})`;
                    layout.yaxis.title = table.axes[2].name;
                }
                await Plotly.newPlot(ref.current, data, layout, {
                    displayModeBar: false,
                    responsive: true,
                });
                if (!cancelled) setReady(true);
            } catch (e) {
                if (!cancelled) setErr(e?.message || String(e));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [table]);

    if (err) {
        return (
            <div
                className="text-[11px] font-mono text-ux-text-3"
                data-testid="aero-quickplot-fallback"
            >
                Plot unavailable ({err}). Table summary: {table?.axes?.length}-D,
                {" "}
                {Array.isArray(table?.values) ? table.values.length : 1} rows.
            </div>
        );
    }
    return (
        <div
            ref={ref}
            data-testid="aero-quickplot"
            className="w-full h-48 bg-ux-bg-2 rounded border border-ux-border-1"
            data-plot-ready={ready ? "true" : "false"}
        />
    );
}

// ---------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------
export default function AeroTableEditor({
    coefficient = "CL",
    table,
    onChange,
    readOnly = false,
}) {
    const [csvText, setCsvText] = useState("");
    const [err, setErr] = useState(null);
    const [okFlash, setOkFlash] = useState(false);

    const summary = useMemo(() => {
        if (!table?.axes?.length) return null;
        return table.axes
            .map((a) => `${a.name}[${a.values.length}]`)
            .join(" × ");
    }, [table]);

    const handleApply = () => {
        try {
            const parsed = parseCsv(csvText);
            onChange?.(parsed);
            setErr(null);
            setOkFlash(true);
            setTimeout(() => setOkFlash(false), 900);
        } catch (e) {
            setErr(e?.message || String(e));
        }
    };

    return (
        <div
            className="space-y-3"
            data-testid={`aero-editor-${coefficient}`}
        >
            <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                    Coefficient
                </span>
                <span className="text-[13px] font-medium text-ux-text-1">{coefficient}</span>
                {summary && (
                    <span
                        className="text-[11px] font-mono text-ux-text-3 ml-auto"
                        data-testid={`aero-editor-summary-${coefficient}`}
                    >
                        {summary}
                    </span>
                )}
            </div>

            {table ? (
                <QuickPlot table={table} />
            ) : (
                <div
                    className="text-[12px] text-ux-text-3 bg-ux-bg-2 border border-dashed border-ux-border-1 rounded p-4"
                    data-testid={`aero-editor-empty-${coefficient}`}
                >
                    No {coefficient} table yet. Paste a CSV below to populate.
                </div>
            )}

            {!readOnly && (
                <div>
                    <label
                        htmlFor={`aero-csv-${coefficient}`}
                        className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3"
                    >
                        Paste CSV
                    </label>
                    <Textarea
                        id={`aero-csv-${coefficient}`}
                        value={csvText}
                        onChange={(e) => setCsvText(e.target.value)}
                        className="mt-1 font-mono text-[12px] bg-ux-bg-2 border-ux-border-1 min-h-[96px]"
                        placeholder={`alpha_deg, ${coefficient}\n-10, -0.6\n0, 0.2\n10, 1.0`}
                        data-testid={`aero-csv-input-${coefficient}`}
                    />
                    <div className="mt-2 flex items-center gap-2">
                        <Button
                            type="button"
                            onClick={handleApply}
                            size="sm"
                            data-testid={`aero-apply-${coefficient}`}
                        >
                            <UploadCloud className="w-3.5 h-3.5 mr-1" />
                            Apply CSV
                        </Button>
                        {okFlash && (
                            <span
                                className="inline-flex items-center gap-1 text-[12px] text-ux-success"
                                data-testid={`aero-applied-${coefficient}`}
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Applied.
                            </span>
                        )}
                        {err && (
                            <span
                                className="inline-flex items-center gap-1 text-[12px] text-ux-danger"
                                role="alert"
                                data-testid={`aero-error-${coefficient}`}
                            >
                                <AlertTriangle className="w-3.5 h-3.5" /> {err}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export { parseCsv };
